import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../config';
import { useTheme } from '../context/ThemeContext';
import { getReferralCode, setPlatformCode } from '../platform';

interface ReferralInfo {
  exists: boolean;
  name: string | null;
  rewardAmount?: string;
  platformCode?: string | null;
}

/**
 * "«name» sent you $5" banner shown to an invited visitor on the guest landing
 * and register pages. Reads the referral code captured from the ?ref= link, asks
 * the public /referral/info endpoint who the referrer is + the welcome amount,
 * and also binds the guest to the referrer's tenant (setPlatformCode) so the
 * games preview shows the right operator's catalog before signup. Renders
 * nothing when there's no code, the code is unknown, or the lookup fails — it is
 * purely additive and never blocks the page.
 */
export default function ReferralBanner() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [info, setInfo] = useState<ReferralInfo | null>(null);

  useEffect(() => {
    const code = getReferralCode();
    if (!code) return;
    let cancelled = false;
    axios
      .get<ReferralInfo>(`${API_BASE}/referral/info/${encodeURIComponent(code)}`)
      .then((res) => {
        if (cancelled || !res.data?.exists) return;
        setInfo(res.data);
        // Preview the referrer's tenant catalog before the visitor signs up.
        if (res.data.platformCode) setPlatformCode(res.data.platformCode);
      })
      .catch(() => {
        /* unknown/expired code — banner stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!info?.name) return null;

  const amount = info.rewardAmount ? `$${info.rewardAmount}` : null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '0 auto 14px',
        maxWidth: 420,
        padding: '12px 14px',
        borderRadius: 14,
        backgroundColor: colors.accentSoft,
        border: `1px solid ${colors.accentSoft}`,
      }}
    >
      <span style={{ fontSize: 20 }} aria-hidden>🎁</span>
      <div style={{ fontSize: 14, fontWeight: 700, color: colors.text, lineHeight: 1.4 }}>
        {amount
          ? t('auth.referralBanner.sentYou', { name: info.name, amount })
          : t('auth.referralBanner.invitedYou', { name: info.name })}
      </div>
    </div>
  );
}
